import { Option } from '@spike/model';
import isEmpty from 'lodash/isEmpty';
import ListItem from './ListItem';
import styles from './MultipleListContent.module.scss';
import Search from './Search';
import { useMemo, useState } from 'react';
import useNonInitialEffect from '@versiondos/hooks';

interface MultipleListContentProps<T> {
    options: Array<Option<T>>;
    selectedIds: Array<T>;
    className?: string;
    allOptionLabel?: string;
    enableSearch?: boolean;
    onChange?: (selectedIds: Array<T>) => void;
}

export const MultipleListContent = <T,>(props: MultipleListContentProps<T>) => {
    const [searchTokens, setSearchTokens] = useState<Array<string>>([]);

    const [selectedIds, setSelectedIds] = useState(props.selectedIds);
    const [selectedAll, setSelectedAll] = useState(props.selectedIds.length === props.options.length);

    useNonInitialEffect(() => {
        if (selectedIds !== props.selectedIds) {
            setSelectedIds(props.selectedIds);
        }
    }, [props.selectedIds]);

    useNonInitialEffect(() => {
        props.onChange?.(selectedIds);
    }, [selectedIds]);

    const searchHandler = (text: string) => {
        setSearchTokens(text.toLowerCase().split(' '));
    };

    const options = useMemo(() => {
        if (searchTokens.length === 0) {
            return [...props.options];
        } else {
            return props.options.filter(option => {
                const optionTokens = option.name.toLowerCase().split(' ');
                return searchTokens.some(searchToken =>
                    optionTokens.some(optionToken => optionToken.startsWith(searchToken))
                );
            });
        }
    }, [searchTokens]);

    const clickHandler = (optionId: T, selected: boolean) => {
        setSelectedAll(selected ? selectedIds.length + 1 === props.options.length : false);
        setSelectedIds(prev =>
            selected
                ? [...prev.filter(selectedId => selectedId !== optionId), optionId]
                : prev.filter(selectedId => selectedId !== optionId)
        );
    };

    const clickAllHandler = (selected: boolean) => {
        setSelectedAll(selected);
        setSelectedIds(selected ? [...props.options.map(option => option.id)] : []);
    };

    return (
        <>
            {props.enableSearch && (
                <Search
                    className={styles.search}
                    onSearch={searchHandler}
                />
            )}
            {!isEmpty(props.allOptionLabel) && (
                <div className={styles.allContainer}>
                    <ListItem
                        label={props.allOptionLabel ?? 'All'}
                        selected={selectedAll}
                        onClick={clickAllHandler}
                    />
                </div>
            )}
            <div className={styles.optionsContainer}>
                {options.map(option => (
                    <ListItem
                        key={String(option.id)}
                        label={option.name}
                        selected={selectedIds.includes(option.id)}
                        onClick={selected => clickHandler(option.id, selected)}
                        className={styles.item}
                    />
                ))}
            </div>
        </>
    );
};

export default MultipleListContent;
